import BackendConfig from "src/backendConfig";

export function CreateProject(projectJson, language = "pt-BR") {
  function RecursiveTranslate(value, lang) {
    if (typeof value !== "object" || !value)
      return value;

    if (Array.isArray(value))
      return value.map(x => RecursiveTranslate(x, lang));

    if (Object.hasOwn(value, "en-US") || Object.hasOwn(value, "pt-BR"))
      return value[lang] ?? value["pt-BR"] ?? value["en-US"];

    return Object.fromEntries(Object.entries(value).map(([key, value]) => {
      return [key, RecursiveTranslate(value, lang)];
    }));
  }

  function IsNewApiProjectShape(value) {
    return Boolean(
      value
      && typeof value === "object"
      && Object.hasOwn(value, "project_id")
    );
  }

  function ExtractApiProject(value) {
    if (
      value
      && typeof value === "object"
      && Array.isArray(value.data)
      && value.data.length > 0
      && typeof value.data[0] === "object"
    ) {
      return value.data[0];
    }

    return value;
  }

  function EnsureObject(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function EnsureArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function EnsureLongDescription(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim()) return [value];
    return [];
  }

  function NormalizeImage(input) {
    if (!input) return { url: "" };
    if (typeof input === "string") return { url: input };
    if (typeof input === "object") {
      return {
        ...input,
        url: input.url
          ?? input.src
          ?? input.image_url
          ?? input.profile_photo
          ?? input.photo_url
          ?? input.profile_image
          ?? input.logo_url
          ?? ""
      };
    }
    return { url: "" };
  }

  function NormalizeImages(images) {
    return EnsureArray(images).map((image) => NormalizeImage(image));
  }

  function NormalizeResources(resources) {
    return EnsureArray(resources).map((resource) => {
      const entry = EnsureObject(resource);
      return {
        ...entry,
        url: resolveUrl(entry.url),
      };
    });
  }

  function PickFirstImage(candidates) {
    for (const candidate of EnsureArray(candidates)) {
      const image = NormalizeImage(candidate);
      if (image.url) return image;
    }

    return { url: "" };
  }

  function NormalizeNewApiProject(value) {
    const localization = EnsureObject(value.localization);
    const token = EnsureObject(value.token);
    const apiIssuer = EnsureObject(value.issuer);
    const apiIssuerLocalization = EnsureObject(apiIssuer.localization);
    const localizationIssuer = EnsureObject(localization.issuer);
    const galleryImages = NormalizeImages(localization.images);
    const thumbnail = NormalizeImage(localization.project_thumbnail ?? galleryImages[0]);
    const issuerProfileImage = PickFirstImage([
      apiIssuerLocalization.profile_photo,
      apiIssuerLocalization.profile_image,
      apiIssuerLocalization.profileImage,
      apiIssuerLocalization.photo,
      apiIssuerLocalization.logo,
      apiIssuer.profileImage,
      apiIssuer.profile_image,
      apiIssuer.profile_photo,
      apiIssuer.photo,
      apiIssuer.logo,
      localizationIssuer.profileImage,
      localizationIssuer.profile_image,
      localizationIssuer.profile_photo,
      localizationIssuer.photo,
      localizationIssuer.logo,
      token.issuer_profile_image,
      token.issuer_profile_photo,
      token.issuer_photo,
      token.issuer_logo,
      token.profile_image,
      localization.issuer_profile_image,
      localization.issuer_profile_photo,
      localization.issuer_photo,
      localization.issuer_logo,
      localization.project_thumbnail,
      galleryImages[0],
    ]);
    const normalizedImages = galleryImages.length > 0
      ? galleryImages
      : (thumbnail.url ? [thumbnail] : []);

    return {
      token: {
        projectId: value.project_id,
        projectLabel: localization.title ?? token.project_label ?? "",
        projectType: localization.asset_type ?? token.project_type ?? "",
        quantity: token.token_quantity ?? value.max_investment ?? 0,
        currency: value.currency ?? null,
        value: value.token_price ?? 0,
      },
      page: {
        title: localization.title ?? token.project_label ?? "",
        shortDescription: localization.short_description ?? token.description ?? "",
        longDescription: EnsureLongDescription(localization.long_description),
        thumbnail,
        price: value.token_price ?? 0,
        tokenFees: {
          tax: null,
          premium: value.token_premium ?? 0,
        },
        currency: value.currency ?? null,
        minTokens: value.min_investment ?? 0,
        maxTokens: value.max_investment ?? null,
        projectDuration: {
          startDate: value.start_date ?? null,
          endDate: value.end_date ?? null,
        },
        tags: EnsureArray(localization.tags),
        resources: EnsureArray(localization.resources),
        images: normalizedImages,
        issuer: {
          name: apiIssuerLocalization.name ?? token.issuer_name ?? "",
          about: apiIssuerLocalization.about ?? "",
          location: apiIssuerLocalization.location ?? token.project_country ?? "",
          industry: apiIssuerLocalization.industry ?? token.project_type ?? "",
          assetTypes: apiIssuerLocalization.assetTypes ?? apiIssuerLocalization.industry ?? token.project_type ?? "",
          profileImage: issuerProfileImage,
        },
      },
    };
  }

  function resolveUrl(url) {
    if (!url || typeof url !== "string") return "";
    if (/^(https?:)?\/\//.test(url) || url.startsWith("data:")) return url;
    if (url.startsWith("/nex-resources/")) return `${BackendConfig.httpBackend}${url}`;
    return (process.env.PUBLIC_URL || "") + url;
  }

  const sourceProjectJson = ExtractApiProject(projectJson);
  const fromApi = IsNewApiProjectShape(sourceProjectJson);
  if (!fromApi) {
    const translatedLegacy = RecursiveTranslate(sourceProjectJson, language || "pt-BR");
    const legacyPage = EnsureObject(translatedLegacy?.page);
    const legacyToken = EnsureObject(translatedLegacy?.token);
    const legacyIssuer = EnsureObject(legacyPage.issuer);
    const legacyIssuerProfile = NormalizeImage(legacyIssuer.profileImage);
    const legacyThumb = NormalizeImage(legacyPage.thumbnail);
    const legacyImages = NormalizeImages(legacyPage.images);

    return {
      projectId: translatedLegacy?.projectId ?? legacyToken.projectId ?? legacyToken.project_id ?? null,
      page: {
        ...legacyPage,
        shortDescription: legacyPage.shortDescription ?? "",
        longDescription: EnsureLongDescription(legacyPage.longDescription),
        tags: EnsureArray(legacyPage.tags),
        resources: NormalizeResources(legacyPage.resources),
        thumbnail: { ...legacyThumb, url: resolveUrl(legacyThumb.url) },
      },
      token: {
        ...legacyToken,
        projectId: legacyToken.projectId ?? legacyToken.project_id ?? null,
        projectLabel: legacyToken.projectLabel ?? legacyPage.title ?? "",
      },
      issuer: {
        ...legacyIssuer,
        profileImage: {
          ...legacyIssuerProfile,
          url: resolveUrl(legacyIssuerProfile.url),
        },
      },
      images: legacyImages.map((img) => ({ ...img, url: resolveUrl(img.url) })),
      unresolved: sourceProjectJson,
    };
  }
  const fallbackFromApi = NormalizeNewApiProject(sourceProjectJson);

  const translated = RecursiveTranslate(fallbackFromApi, language || "pt-BR");

  const page = EnsureObject(translated.page);
  const token = EnsureObject(translated.token);
  const issuer = EnsureObject(page.issuer);
  const apiLocalization = EnsureObject(sourceProjectJson.localization);
  const apiToken = EnsureObject(sourceProjectJson.token);
  const apiIssuer = EnsureObject(sourceProjectJson.issuer);
  const apiIssuerLocalization = EnsureObject(apiIssuer.localization);
  const apiLocalizationIssuer = EnsureObject(apiLocalization.issuer);
  const pageLongDescription = EnsureLongDescription(page.longDescription);
  const apiLongDescription = EnsureLongDescription(apiLocalization.long_description);
  const pageTags = EnsureArray(page.tags);
  const apiTags = EnsureArray(apiLocalization.tags);
  const pageResources = EnsureArray(page.resources);
  const apiResources = EnsureArray(apiLocalization.resources);
  const mergedResources = pageResources.length > 0 ? pageResources : apiResources;

  const apiImages = NormalizeImages(apiLocalization.images);
  const pageImages = NormalizeImages(page.images);
  const mergedImages = apiImages.length > 0 ? apiImages : pageImages;

  const apiThumbnail = NormalizeImage(apiLocalization.project_thumbnail ?? apiImages[0]);
  const pageThumbnail = NormalizeImage(page.thumbnail);
  const mergedThumbnail = apiThumbnail.url ? apiThumbnail : pageThumbnail;

  const resolvedImages = mergedImages.map(img => ({ ...img, url: resolveUrl(img.url) }));
  const resolvedResources = NormalizeResources(mergedResources);

  const resolvedIssuerProfileImage = PickFirstImage([
    apiIssuerLocalization.profile_photo,
    apiIssuerLocalization.profile_image,
    apiIssuerLocalization.profileImage,
    apiIssuerLocalization.photo,
    apiIssuerLocalization.logo,
    apiIssuer.profileImage,
    apiIssuer.profile_image,
    apiIssuer.profile_photo,
    apiIssuer.photo,
    apiIssuer.logo,
    apiLocalizationIssuer.profileImage,
    apiLocalizationIssuer.profile_image,
    apiLocalizationIssuer.profile_photo,
    apiLocalizationIssuer.photo,
    apiLocalizationIssuer.logo,
    apiToken.issuer_profile_image,
    apiToken.issuer_profile_photo,
    apiToken.issuer_photo,
    apiToken.issuer_logo,
    apiToken.profile_image,
    apiLocalization.issuer_profile_image,
    apiLocalization.issuer_profile_photo,
    apiLocalization.issuer_photo,
    apiLocalization.issuer_logo,
    issuer.profileImage,
    mergedThumbnail,
    mergedImages[0],
  ]);

  return {
    projectId: sourceProjectJson.project_id,
    page: {
      ...page,
      title: page.title ?? apiLocalization.title ?? apiToken.project_label ?? "",
      shortDescription: page.shortDescription ?? apiLocalization.short_description ?? apiToken.description ?? "",
      longDescription: pageLongDescription.length > 0 ? pageLongDescription : apiLongDescription,
      tags: pageTags.length > 0 ? pageTags : apiTags,
      resources: resolvedResources,
      currency: sourceProjectJson.currency ?? page.currency ?? token.currency ?? null,
      price: sourceProjectJson.token_price ?? page.price ?? token.value ?? 0,
      tokenFees: {
        tax: page.tokenFees?.tax ?? null,
        premium: sourceProjectJson.token_premium ?? page.tokenFees?.premium ?? null,
      },
      minTokens: sourceProjectJson.min_investment ?? page.minTokens ?? 0,
      maxTokens: sourceProjectJson.max_investment ?? page.maxTokens ?? null,
      projectDuration: {
        startDate: sourceProjectJson.start_date ?? page.projectDuration?.startDate ?? null,
        endDate: sourceProjectJson.end_date ?? page.projectDuration?.endDate ?? null,
      },
      thumbnail: {
        ...mergedThumbnail,
        url: resolveUrl(mergedThumbnail.url)
      },
      images: resolvedImages,
    },
    token: {
      ...token,
      projectId: sourceProjectJson.project_id ?? token.projectId ?? null,
      projectLabel: token.projectLabel ?? page.title ?? apiLocalization.title ?? apiToken.project_label ?? "",
      projectType: token.projectType ?? apiLocalization.asset_type ?? apiToken.project_type ?? "",
      quantity: token.quantity ?? apiToken.token_quantity ?? apiToken.tokens_left ?? page.maxTokens ?? 0,
      currency: sourceProjectJson.currency ?? token.currency ?? page.currency ?? null,
      tokensLeft: sourceProjectJson.tokens_left ?? token.tokensLeft ?? apiToken.tokens_left ?? apiToken.token_quantity ?? page.maxTokens ?? 0,
      value: sourceProjectJson.token_price ?? token.value ?? page.price ?? 0,
    },
    issuer: {
      ...issuer,
      name: apiIssuerLocalization.name ?? issuer.name ?? apiToken.issuer_name ?? "",
      about: apiIssuerLocalization.about ?? issuer.about ?? "",
      location: apiIssuerLocalization.location ?? issuer.location ?? apiToken.project_country ?? "",
      industry: apiIssuerLocalization.industry ?? issuer.industry ?? apiToken.project_type ?? "",
      assetTypes: apiIssuerLocalization.assetTypes ?? apiIssuerLocalization.industry ?? issuer.assetTypes ?? apiToken.project_type ?? "",
      profileImage: {
        ...resolvedIssuerProfileImage,
        url: resolveUrl(resolvedIssuerProfileImage.url)
      }
    },
    images: resolvedImages,

    unresolved: sourceProjectJson
  };
}